import { type Ref, type RefCallback, useCallback, useEffect, useState } from 'react';

import { assignRef } from '../utils/merge-refs';
import { useObservabilitySafe } from './useObservabilitySafe';

type ImpressionEventType =
  | 'accordionViewEvent'
  | 'boxViewEvent'
  | 'buttonViewEvent'
  | 'cardViewEvent'
  | 'checkboxViewEvent'
  | 'imageViewEvent'
  | 'inputViewEvent'
  | 'linkViewEvent'
  | 'radioViewEvent'
  | 'textViewEvent';

/**
 * Returns a ref callback that fires a one-time `*ViewEvent` when the attached
 * element first enters the viewport. Merge-free for callers: the consumer's
 * ref is composed in, so a component passes the result straight to its element.
 *
 * Two properties matter:
 * - **Bundle isolation.** `react-intersection-observer` is loaded via a dynamic
 *   `import()` INSIDE the effect, so it sits in an on-demand chunk. A component
 *   that merely offers view tracking never pulls rio into its static bundle, and
 *   a consumer who leaves `active` false never loads it.
 * - **No remount.** The observer attaches to the element the component already
 *   renders (via the returned ref) — the element is never wrapped in Suspense or
 *   cloned into a new subtree, so uncontrolled value, focus, caret, and scroll
 *   are never disturbed.
 *
 * The observed element is tracked as state (not a ref) because the effect that
 * sets up the observer depends on it — `setNode` runs from the ref callback at
 * attach time, and the effect re-runs when the node changes.
 */
export function useImpressionRef<T extends Element>(
  active: boolean,
  analyticsName: string | undefined,
  eventName: ImpressionEventType,
  consumerRef: Ref<T>,
  // 0 = fire as soon as any part of the element is visible. Avoids the
  // threshold:1 trap where an element taller/wider than the viewport can never
  // reach 100% visibility and its impression never fires. Callers can pass a
  // stricter fraction if they want "mostly visible" semantics. Clamped to the
  // IntersectionObserver-legal 0–1 range — out-of-range values would throw a
  // RangeError that the catch below swallows, silently killing the impression.
  inViewThreshold = 0,
): RefCallback<T> {
  const { trackJourneyEvent } = useObservabilitySafe();
  const [node, setNode] = useState<T | null>(null);

  useEffect(() => {
    if (!active || !analyticsName || !node) return undefined;

    let cancelled = false;
    let unobserve: (() => void) | undefined;

    // rio is loaded lazily so click-only consumers never bundle it.
    void (async () => {
      try {
        const { observe } = await import('react-intersection-observer');
        if (cancelled) return;
        unobserve = observe(
          node,
          (inView) => {
            if (inView) {
              trackJourneyEvent('platform', eventName, { targetName: analyticsName });
              unobserve?.(); // fire once
            }
          },
          { threshold: Math.min(1, Math.max(0, inViewThreshold)) },
        );
      } catch (error) {
        // View tracking is best-effort and must never break the host (a failed
        // chunk load, or an error wiring up the observer). Surface it in dev so
        // it isn't silently lost; don't assert the cause.
        if (process.env.NODE_ENV && process.env.NODE_ENV !== 'production') {
          console.warn('[Atoms] view tracking unavailable:', error);
        }
      }
    })();

    return () => {
      cancelled = true;
      unobserve?.();
    };
  }, [active, analyticsName, node, eventName, inViewThreshold, trackJourneyEvent]);

  // Records the element as state (for the observer effect) and forwards it to
  // the consumer's ref. The forward goes through `assignRef` — an imported
  // helper called at attach time, never during render — so no ref is read or
  // mutated during render. Stable per `consumerRef`.
  return useCallback(
    (element: T | null) => {
      setNode(element);
      assignRef(consumerRef, element);
    },
    [consumerRef],
  );
}
